import { useEffect } from "react";
import { createColumnHelper } from "@tanstack/table-core";
import Typography from "@mui/material/Typography";
import z from "zod";

import { ProfileType } from "core/models/profile";
import { useDebounce } from "core/hooks/useDebounce";
import useFilters from "core/hooks/useFilters";
import dates from "core/dates";
import numbers from "core/numbers";
import strings from "core/strings";

import Box from "ui/atom/Box";
import Icon, { type IconName } from "ui/atom/Icon";
import Image from "ui/atom/Image";
import Button from "ui/base/Button";

import type { FormData } from "features/form/models/types";
import { useForm } from "features/form/fabric";
import { useSettings } from "features/start/fabric";

import type { UserCampaignInfoApi } from "api/models/campaigns";
import type { ContentInfoApi } from "api/models/content";

import type { CardInfo, DepositInfo } from "./models";
import { useStoreUser, type UserAccount } from "./store";
import { useMutateAccount, useMutateCampaign, useMutateDefaultCard, useMutateDeleteCard, useMutateHistory, useMutateProfile, useMutateUser, useQueryCampaigns, useQueryCards, useQueryDeposits, useQueryHistory, useQuerySearch, useQueryUser } from "./data";


export const useUser = () => {
	const store = useStoreUser();
	const { filters, setFilters } = useFilters("/users-layout/users");

	const setUser = (user: UserAccount) => {
		setFilters({ uid: user.id ? user.id : null });
		store.setUser(user);
	};

	return { ...store, filters, setUser };
};

export const useCards = () => {
	const selected = useSelectedAccount();
	const query = useQueryCards(selected.profile?.id);
	const defaultCard = useMutateDefaultCard();
	const deleteCard = useMutateDeleteCard();
	const columnHelper = createColumnHelper<CardInfo>();

	const columns = [
		columnHelper.accessor("id", {
			header: () => "Id",
			size: 20,
			cell: info => info.renderValue()
		}),
		columnHelper.display({
			id: "card",
			header: () => "Card",
			cell: ({ row }) => {
				const icon = strings.toKebabCase(row.original.wallet || "") as IconName;

				return (
					<Box alignItems="center" gap={0.5}>
						{icon && <Icon name={icon} />}
						<Typography>{row.original.brand} ending in {row.original.last4}</Typography>
					</Box>
				);
			}
		}),
		columnHelper.accessor("expiration", {
			header: () => "Exp",
			size: 50,
			cell: info => info.renderValue()
		}),
		columnHelper.display({
			id: "button",
			size: 40,
			header: () => "Actions",
			cell: ({ row }) => { /* todo: move component out of fabric */
				const card = row.original;
				const title = `${card.brand} ending in ${card.last4}`;
				return (
					<Box alignItems="center" gap={0.5}>
						<Button.Confirmation
							icon="credit-card"
							color={card.default ? "light" : "primary"}
							fill={card.default ? "black" : undefined}
							size={21}
							disabled={card.default}
							title="Set card as default"
							text={`${title} will be used for auto-reload and shown first when making deposits.`}
							loading={defaultCard.isPending}
							sx={{ "&.Mui-disabled": { color: "white" } }}
							onClick={() => defaultCard.mutateAsync({ profileId: selected.profile!.id, cardId: card.id })}
						/>
						<Button.Confirmation
							icon="trash-2"
							color="error"
							title="Delete Card"
							size={21}
							text={`Are you sure you want to delete ${title}? This action cannot be undone.`}
							loading={deleteCard.isPending}
							onClick={() => deleteCard.mutateAsync({ profileId: selected.profile!.id, cardId: card.id })}
						/>
					</Box>
				);}
		})
	];

	return	{ ...query, columns };
};

export const useDeposits = () => {
	const selected = useSelectedAccount();
	const query = useQueryDeposits(selected.profile?.id);
	const columnHelper = createColumnHelper<DepositInfo>();

	const columns = [
		columnHelper.accessor("amount", {
			header: () => "Amount",
			cell: info => numbers.format(info.getValue(), "currency", { decimals: 2 })
		}),
		columnHelper.accessor("tokens", {
			header: () => "Tokens",
			cell: (info) => (
				<Box gap={0.5} sx={{ alignItems: "center" }}>
					<Icon.Coin size={16} /> {info.getValue()}
				</Box>
			)
		}),
		columnHelper.accessor("date", {
			header: "Date",
			cell: info => dates.brief(info.getValue())
		}),
		columnHelper.accessor("receiptUrl", {
			header: "Receipt",
			cell: info => <Button.Icon icon="receipt" size={21} href={info.row.original.receiptUrl} target="_blank" rel="noopener" title="View Receipt" />
		})
	];

	return { ...query, columns };
};

export const useHistory = () => {
	const mutation = useMutateHistory();
	const query = useQueryHistory();
	const columnHelper = createColumnHelper<ContentInfoApi>();

	const columns = [
		columnHelper.accessor("thumbUrl", {
			header: () => "Cover",
			minSize: 80,
			cell: info => <Image.Cover size={45} src={info.getValue()!} />
		}),
		columnHelper.accessor("title", {
			header: () => "Title",
			minSize: 130,
			enableResizing: true,
			size: "100%" as unknown as number,
			cell: info => {
				const style = {
					root: {
						display: "-webkit-box",
						overflow: "hidden",
						WebkitLineClamp: "2",
						WebkitBoxOrient: "vertical"
					}
				};
				return <Typography variant="body3" sx={style.root}>{info.getValue()}</Typography>;
			}
		}),
		columnHelper.accessor("purchasedOn", {
			header: "Date",
			size: 50,
			cell: info => dates.brief(info.getValue())
		}),
		columnHelper.accessor("tokens", {
			header: () => "Tokens",
			size: 30,
			cell: (info) => (
				<Box gap={0.5}>
					<Icon name={info.row.original.isCampaign ? "campaign-coin" : "coin"} size={16} /> {info.getValue()}
				</Box>
			)
		}),
		columnHelper.display({
			id: "button",
			size: 30,
			header: () => "Actions",
			cell: ({ row }) => <Button.Confirmation
				title="Undo Unlock"
				text="This will undo this article unlock. We will add back back the tokens to the profile balance"
				icon="undo-2"
				color="primary"
				onClick={() => mutation.mutateAsync({ scuId: row.original.scuId! })}
			/>
		})
	];

	return { ...query, columns };
};

export const useSearchAccounts = (search?: string, stop = false) => {
	const debounced = useDebounce(search, 400);
	return useQuerySearch(debounced || "", stop);
};

export const useSelectedAccount = () => {
	const user = useUser();
	const query = useQueryUser(user.filters.uid); // get from param
	const userMutation = useMutateUser();
	const accountMutation = useMutateAccount();

	const profile = query.data?.profiles?.find(p => p.id.toString() == user.profileId) || query.data?.profiles?.[0];
	const disabled = (!query.data?.email?.value || !query.data?.email?.verified) || (!query.data?.phone?.value || !query.data?.phone?.verified);

	useEffect(() => {
		if (query.isSuccess && !user.user.name) user.setUserFromQuery(query.data?.id, query.data?.name);
	}, [query.data]);

	return {
		...query,
		profile,
		disabled,
		mutation: {
			user: userMutation,
			account: accountMutation
		}

	};
};

const UserSchema = z.object({
	name: z.string().optional(),
	nickname: z.string().optional(),
	email: z.email().optional(),
	phone: z.string().optional()
});

export const useUserInfo = () => {
	const account = useSelectedAccount();
	const userMutation = useMutateUser();
	const accountMutation = useMutateAccount();

	const defaultValues: FormData<typeof UserSchema> = {
		name: account.data?.name || "",
		nickname: account.data?.nickname || "",
		email: account.data?.email?.value,
		phone: numbers.toPhone(account.data?.phone?.value.slice(2))
	};

	const form = useForm({
		defaultValues,
		validators: {
			onSubmit: UserSchema
		},
		canSubmitWhenInvalid: true,
		onSubmit: async ({ value }) => await userMutation.mutateAsync({ name: value.name || "", nickname: value.nickname })
	});

	return {
		...account,
		form,
		accountMutation
	};
};

const ProfileSchema = z.object({
	type: z.enum(ProfileType),
	balance: z.string(),
	reload: z.number(),
	threshold: z.number()
});

export const useProfileInfo = () => {
	const account = useSelectedAccount();
	const settings = useSettings();
	const mutation = useMutateProfile();

	const defaultValues: FormData<typeof ProfileSchema> = {
		type: account.profile?.type || ProfileType.Personal,
		balance: `${account.profile?.balance || 0}`,
		reload: account.profile?.reload || settings.token.default.amount,
		threshold: account.profile?.threshold || settings.token.default.threshold
	};

	const form = useForm({
		defaultValues,
		validators: {
			onSubmit: ProfileSchema
		},
		canSubmitWhenInvalid: true,
		onSubmit: async ({ value }) => {
			const body = {
				profileId: account.profile!.id,
				type: value.type,
				balance: Number(value.balance),
				reload: value.reload,
				threshold: value.threshold
			};

			return await mutation.mutateAsync(body);
		}
	});

	const reloadOptions = {
		threshold: settings.token.options.threshold.map(t => ({ value: t, label: `${t} Tokens` })),
		amount: settings.token.options.amount.map(o => ({ value: o.tokens, label: `${strings.pluralize(o.tokens, "Token")} (${numbers.format(o.price, "currency")})` }))
	};
	const reloadDefault = {
		threshold: reloadOptions.threshold.find(o => o.value === account.profile?.threshold),
		amount: reloadOptions.amount.find(o => o.value === account.profile?.reload)
	};

	return {
		...account,
		mutation,
		form,
		reload: {
			options: reloadOptions,
			default: reloadDefault
		}
	};
};

// CAMPAIGNS
export const useCampaigns = () => {
	const user = useUser();
	const campaigns = useQueryCampaigns(user.filters.uid);
	const columnHelper = createColumnHelper<UserCampaignInfoApi>();
	const mutation = useMutateCampaign();

	const columns = [
		columnHelper.accessor("cover", {
			header: () => "Cover",
			minSize: 80,
			cell: info => <Image.Cover size={45} src={info.getValue()!} />
		}),
		columnHelper.accessor("name", {
			header: () => "Name",
			minSize: 130,
			enableResizing: true,
			size: "100%" as unknown as number,
			cell: info => {
				const style = {
					root: {
						display: "-webkit-box",
						overflow: "hidden",
						WebkitLineClamp: "2",
						WebkitBoxOrient: "vertical"
					}
				};
				return <Typography variant="body3" sx={style.root}>{info.getValue()}</Typography>;
			}
		}),
		columnHelper.accessor("partner.name", {
			header: "Partner",
			minSize: 140,
			cell: info => info.getValue()
		}),
		columnHelper.accessor("balance", {
			header: () => "Balance",
			size: 30,
			cell: (info) => (
				<Box gap={0.5}>
					<Icon.CampaignCoin size={16} /> {info.getValue()}
				</Box>
			)
		}),
		columnHelper.display({
			id: "button",
			size: 30,
			header: () => "Actions",
			cell: ({ row }) => (
				<Box gap={0.5}>
					<Button.Confirmation
						title="Unredeem"
						text="This will unredeem the campaign for this user"
						icon="undo-2"
						color="primary"
						onClick={() => mutation.mutateAsync({ campaignId: row.original.id!, userId: user.user.id! })}
					/>
					<Button.Icon icon="chevron-right" href={row.original.redeemUrl} tab />
				</Box>
			)
		})
	];
	return { ...campaigns, columns };
};